import { useState } from 'react'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { useQuery } from '@tanstack/react-query'
import {
  Loader2, BookOpen, FolderOpen, PlayCircle,
  Plus, ChevronRight, ChevronDown,
} from 'lucide-react'
import { adminApi, type AdminContent } from '@/api/admin'

const LEVEL_ICONS: Record<string, React.ElementType> = {
  curriculum: BookOpen,
  course: FolderOpen,
  module: FolderOpen,
  lesson: PlayCircle,
}

interface ContentPickerTreeProps {
  /** The level the parent wants to attach. e.g. picking a 'course' for a curriculum. */
  childLevel: 'course' | 'module' | 'lesson'
  /** Children already attached to the current parent (rendered greyed out). */
  existingIds: Set<number>
  /** Called when the user clicks "+" on a node of the target level. */
  onPick: (content: AdminContent) => void
}

export function ContentPickerTree({ childLevel, existingIds, onPick }: ContentPickerTreeProps) {
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set())

  const { data: curricula, isLoading } = useQuery({
    queryKey: ['admin-contents-tree', 'curriculum'],
    queryFn: () => adminApi.getContentsByLevel('curriculum'),
  })

  const toggleExpand = (id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    )
  }

  return (
    <div className="text-sm">
      {curricula?.map((curr: AdminContent) => (
        <PickerNode
          key={curr.id}
          content={curr}
          level={0}
          expandedIds={expandedIds}
          onToggle={toggleExpand}
          existingIds={existingIds}
          childLevel={childLevel}
          onPick={onPick}
        />
      ))}
    </div>
  )
}

function PickerNode({
  content, level, expandedIds, onToggle, existingIds, childLevel, onPick,
}: {
  content: AdminContent
  level: number
  expandedIds: Set<number>
  onToggle: (id: number) => void
  existingIds: Set<number>
  childLevel: 'course' | 'module' | 'lesson'
  onPick: (content: AdminContent) => void
}) {
  const isExpanded = expandedIds.has(content.id)
  const isTargetLevel = content.hierarchyLevel === childLevel
  const canExpand = content.hierarchyLevel !== 'lesson'

  const { data: children } = useQuery({
    queryKey: ['admin-content-children', content.id],
    queryFn: () => adminApi.getChildContents(content.id),
    enabled: isExpanded && canExpand,
  })

  const Icon = LEVEL_ICONS[content.hierarchyLevel] || FolderOpen
  const isAlreadyAdded = isTargetLevel && existingIds.has(content.id)

  return (
    <div>
      <div
        className={`flex items-center gap-1.5 py-1.5 px-2 rounded cursor-pointer transition-colors ${
          isAlreadyAdded ? 'opacity-40' : 'hover:bg-slate-50'
        }`}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
        onClick={() => canExpand ? onToggle(content.id) : undefined}
      >
        {canExpand
          ? (isExpanded
              ? <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              : <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />)
          : <span className="w-3.5 shrink-0" />}
        <Icon className="w-3.5 h-3.5 shrink-0 text-slate-400" />
        <span className="truncate text-slate-700 flex-1"><ContentMetadataText text={content.title} /></span>

        {isTargetLevel && !isAlreadyAdded && (
          <button
            onClick={(e) => { e.stopPropagation(); onPick(content) }}
            className="p-0.5 text-blue-500 hover:bg-blue-50 rounded shrink-0"
            title="선택"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        )}
        {isAlreadyAdded && (
          <span className="text-[10px] text-slate-400 shrink-0">이미 추가됨</span>
        )}
      </div>

      {isExpanded && children?.map((child: AdminContent) => (
        <PickerNode
          key={child.id}
          content={child}
          level={level + 1}
          expandedIds={expandedIds}
          onToggle={onToggle}
          existingIds={existingIds}
          childLevel={childLevel}
          onPick={onPick}
        />
      ))}
    </div>
  )
}
